/**
 * Diagnostics tab — admin view of every employee's agent liveness.
 *
 * The whole point: tell whether a "Mất kết nối" tile is the agent's fault
 * (process not running on the NV box) or the BE's fault (without RDP-ing
 * into every machine). Each row shows lastHeartbeat, lastScreenshot,
 * lastActivity, currentApp, agentVersion, hostname.
 *
 * Auto-refreshes every 30s. Buttons filter by status. No pagination
 * UI needed for < 100 NVs — pageSize 200 covers current scale.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Eye, Search, Wifi, WifiOff, AlertTriangle, Clock } from 'lucide-react'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { diagnosticsService } from '@/services/diagnostics.service'
import { formatRelativeTime } from '@/lib/format'
import type { AgentDiag } from '@monitor/shared'

type StatusFilter = 'all' | 'online' | 'offline' | 'stale'

export default function DiagnosticsTab() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['diagnostics'],
    queryFn: () => diagnosticsService.list({ pageSize: 200 }),
    refetchInterval: 30_000,
  })

  const filtered = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    return data.items.filter((row) => {
      if (q) {
        const hay = `${row.employeeCode} ${row.fullName} ${row.hostname ?? ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      if (statusFilter === 'online' && !row.isOnline) return false
      if (statusFilter === 'offline' && row.isOnline) return false
      if (
        statusFilter === 'stale' &&
        !(row.isOnline && (row.secsSinceLastSignal ?? 0) > 300)
      )
        return false
      return true
    })
  }, [data, search, statusFilter])

  return (
    <div className="container space-y-4 py-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Tổng NV"
          value={data?.summary.total ?? 0}
          icon={<Clock className="h-4 w-4" />}
        />
        <KpiCard
          label="Online"
          value={data?.summary.online ?? 0}
          tone="success"
          icon={<Wifi className="h-4 w-4" />}
        />
        <KpiCard
          label="Offline"
          value={data?.summary.offline ?? 0}
          tone="danger"
          icon={<WifiOff className="h-4 w-4" />}
        />
        <KpiCard
          label="Stale (DB online, no signal)"
          value={data?.summary.stale ?? 0}
          tone="warning"
          icon={<AlertTriangle className="h-4 w-4" />}
        />
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle>
            Chẩn đoán agent {isFetching && <span className="ml-2 text-xs text-muted-foreground">(đang tải…)</span>}
          </CardTitle>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Tìm NV, hostname…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-56 pl-8"
              />
            </div>
            <div className="flex gap-1 rounded-md border p-0.5">
              {(['all', 'online', 'offline', 'stale'] as const).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={statusFilter === s ? 'default' : 'ghost'}
                  onClick={() => setStatusFilter(s)}
                >
                  {labelFor(s)}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="p-2">Mã NV</th>
                    <th className="p-2">Họ tên</th>
                    <th className="p-2">Hostname</th>
                    <th className="p-2">Version</th>
                    <th className="p-2">IP</th>
                    <th className="p-2">App hiện tại</th>
                    <th className="p-2">Last screenshot</th>
                    <th className="p-2">Last heartbeat</th>
                    <th className="p-2">Trạng thái</th>
                    <th className="p-2 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <Row key={row.id} row={row} />
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={10} className="p-4 text-center text-muted-foreground">
                        Không có NV nào khớp bộ lọc.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function Row({ row }: { row: AgentDiag }) {
  const online = row.isOnline
  const stale = online && (row.secsSinceLastSignal ?? 0) > 300
  const lastHb =
    row.lastHeartbeat
      ? formatRelativeTime(row.lastHeartbeat)
      : 'chưa từng'
  const lastSs =
    row.lastScreenshotAt
      ? formatRelativeTime(row.lastScreenshotAt)
      : '—'
  return (
    <tr className="border-b hover:bg-muted/40">
      <td className="p-2 font-mono text-xs">{row.employeeCode}</td>
      <td className="p-2 font-medium">{row.fullName}</td>
      <td className="p-2 font-mono text-xs">{row.hostname ?? '—'}</td>
      <td className="p-2 text-xs">{row.agentVersion ?? '—'}</td>
      <td className="p-2 font-mono text-xs">{row.ipAddress ?? '—'}</td>
      <td className="p-2 max-w-[180px] truncate text-xs" title={row.currentTitle ?? ''}>
        {row.currentApp ?? '—'}
      </td>
      <td className="p-2 text-xs">{lastSs}</td>
      <td className="p-2 text-xs">{lastHb}</td>
      <td className="p-2">
        {stale ? (
          <Badge variant="warning">Stale</Badge>
        ) : online ? (
          <Badge variant="success">Online</Badge>
        ) : (
          <Badge variant="secondary">Offline</Badge>
        )}
      </td>
      <td className="p-2 text-right">
        <Link
          to={`/diagnostics/${row.id}`}
          aria-label="Chi tiết"
          className={buttonVariants({ variant: 'ghost', size: 'icon' })}
        >
          <Eye className="h-4 w-4" />
        </Link>
      </td>
    </tr>
  )
}

function KpiCard({
  label,
  value,
  icon,
  tone = 'default',
}: {
  label: string
  value: number
  icon: React.ReactNode
  tone?: 'default' | 'success' | 'warning' | 'danger'
}) {
  const toneClass = {
    default: 'text-foreground',
    success: 'text-green-600',
    warning: 'text-amber-600',
    danger: 'text-red-600',
  }[tone]
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className={`text-2xl font-semibold ${toneClass}`}>{value}</div>
        </div>
        <div className="text-muted-foreground">{icon}</div>
      </CardContent>
    </Card>
  )
}

function labelFor(s: StatusFilter): string {
  switch (s) {
    case 'all':
      return 'Tất cả'
    case 'online':
      return 'Online'
    case 'offline':
      return 'Offline'
    case 'stale':
      return 'Stale'
  }
}
